import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { useValidarConformidade } from './useConformidade';

export interface EmpCronogramaVersao {
  id: string;
  contrato_id: string;
  numero_versao: number;
  descricao: string | null;
  motivo_revisao: string | null;
  status: string;
  data_base: string | null;
  importacao_id: string | null;
  planilha_versao_id: string | null;
  total_atividades: number;
  total_marcos: number;
  duracao_total_dias: number;
  data_inicio_prevista: string | null;
  data_fim_prevista: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface EmpCronogramaAtividade {
  id: string;
  versao_id: string;
  codigo_wbs: string;
  nome: string;
  nivel: number;
  atividade_pai_id: string | null;
  tipo: string;
  duracao_dias: number;
  data_inicio_prevista: string | null;
  data_fim_prevista: string | null;
  predecessoras: string[] | null;
  planilha_item_id: string | null;
  peso_fisico: number | null;
  peso_relativo: number | null;
  peso_modelo: string;
  ordem: number;
  status: string;
  origem: string;
  is_leaf_node?: boolean;
  quantidade_takeoff?: number | null;
  unidade_takeoff?: string | null;
  metodo_medicao?: string | null;
}

export interface EmpCronogramaMarco {
  id: string;
  versao_id: string;
  atividade_id: string | null;
  nome: string;
  tipo: string;
  data_prevista: string;
  descricao: string | null;
  obrigatorio: boolean;
  ordem: number;
}

export interface VinculoReport {
  total_folhas: number;
  vinculadas: number;
  ja_vinculadas?: number;
  novas_vinculacoes?: number;
  nao_vinculadas: number;
  cobertura_itens: number;
  cobertura_financeira: number;
  valor_total_planilha: number;
  valor_total_vinculado: number;
  valor_nao_vinculado: number;
  bac_calculado: number;
  divergencia_bac: number;
  itens_nao_vinculados: Array<{ codigo_wbs: string; codigo_normalizado: string }>;
  itens_ambiguos: Array<{ codigo_wbs: string; codigo_normalizado: string; matches?: number; tipo?: string; motivo?: string }>;
  itens_criticos_sem_vinculo: Array<{ codigo_item: string; valor: number; percentual: number }>;
  codigos_colisao?: Array<{ codigo_normalizado: string; codigos_originais: string[]; count: number }>;
  aplicado?: boolean;
}

export function useEmpCronogramaVersoes(contratoId: string) {
  return useQuery({
    queryKey: ['emp-cronograma-versoes', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_cronograma_versoes')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('numero_versao', { ascending: false });
      if (error) throw error;
      return data as EmpCronogramaVersao[];
    },
    enabled: !!contratoId,
  });
}

export function useEmpCronogramaDetalhes(versaoId: string | null) {
  const atividadesQuery = useQuery({
    queryKey: ['emp-cronograma-atividades', versaoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_cronograma_atividades')
        .select('*')
        .eq('versao_id', versaoId!)
        .order('ordem', { ascending: true });
      if (error) throw error;
      return data as EmpCronogramaAtividade[];
    },
    enabled: !!versaoId,
  });

  const marcosQuery = useQuery({
    queryKey: ['emp-cronograma-marcos', versaoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_cronograma_marcos')
        .select('*')
        .eq('versao_id', versaoId!)
        .order('data_prevista', { ascending: true });
      if (error) throw error;
      return data as EmpCronogramaMarco[];
    },
    enabled: !!versaoId,
  });

  return {
    atividades: atividadesQuery.data || [],
    marcos: marcosQuery.data || [],
    isLoading: atividadesQuery.isLoading || marcosQuery.isLoading,
  };
}

export function useEmpCronogramaImport() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const validarConformidade = useValidarConformidade();

  return useMutation({
    mutationFn: async (payload: {
      contrato_id: string;
      descricao?: string;
      motivo_revisao?: string;
      data_base?: string;
      planilha_versao_id?: string;
      atividades: Array<{
        codigo_wbs: string; nome: string; nivel: number;
        pai_wbs?: string; tipo: 'resumo' | 'atividade' | 'marco';
        duracao_dias?: number; data_inicio_prevista?: string;
        data_fim_prevista?: string; predecessoras?: string[];
        planilha_item_codigo?: string; peso_fisico?: number | null; ordem?: number;
        quantidade_takeoff?: number | null; unidade_takeoff?: string;
      }>;
      marcos?: Array<{
        nome: string; tipo: 'entrega' | 'medicao' | 'aprovacao' | 'externo' | 'outro';
        data_prevista: string; atividade_wbs?: string;
        descricao?: string; obrigatorio?: boolean; ordem?: number;
      }>;
      ativar_imediatamente?: boolean;
      preservar_execucao?: boolean;
      skip_code_validation?: boolean;
    }) => {
      // CRITICAL: Enforce planilha_versao_id on client side too
      if (!payload.planilha_versao_id) {
        throw new Error('Planilha orçamentária é obrigatória para importar cronograma. Importe a planilha primeiro.');
      }
      const resp = await supabase.functions.invoke('importar-cronograma', { body: payload });
      if (resp.error) throw new Error(resp.error.message || 'Erro na importação');
      const data = resp.data;
      if (data?.error) throw new Error(data.error);
      if (!data?.versao_id) throw new Error('Resposta inesperada: ' + JSON.stringify(data));
      const warnings: string[] = data?.warnings || [];
      return { ...data, contrato_id: payload.contrato_id, warnings };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['emp-cronograma-versoes'] });

      const report: VinculoReport | undefined = data.vinculo_report;
      const desc = report
        ? `${data.atividades_sucesso} atividades, ${data.marcos_sucesso} marcos | Cobertura financeira: ${report.cobertura_financeira}%`
        : `${data.atividades_sucesso} atividades, ${data.marcos_sucesso} marcos`;

      toast({
        title: `Cronograma v${data.numero_versao} importado`,
        description: desc,
      });

      // Show pre-persistence warnings
      const warnings: string[] = data.warnings || [];
      if (warnings.length > 0) {
        toast({
          title: `⚠ Alertas de integridade (${warnings.length})`,
          description: warnings.join('; '),
          variant: 'destructive',
        });
      }

      // Auto-trigger cross-validation
      validarConformidade.mutate({
        contrato_id: data.contrato_id,
        trigger_type: 'auto_cronograma',
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro na importação', description: err.message, variant: 'destructive' });
    },
  });
}

export function useEmpCronogramaAtivar() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (versaoId: string) => {
      // 1. Run activation gates FIRST
      const { data: gatesRaw, error: gatesError } = await supabase.rpc(
        'emp_validar_gates_ativacao',
        { p_versao_id: versaoId }
      );

      if (gatesError) throw new Error('Erro ao validar gates: ' + gatesError.message);

      const gatesResult = gatesRaw as unknown as { bloqueado: boolean; motivos: string[]; gates: Record<string, unknown> };

      if (gatesResult?.bloqueado) {
        const motivos = Array.isArray(gatesResult.motivos)
          ? gatesResult.motivos.join('; ')
          : String(gatesResult.motivos);
        throw new Error(`Ativação bloqueada: ${motivos}`);
      }

      // 2. Fetch contract info (tipo_medicao, valor_global) via the versao
      const { data: versaoData, error: versaoErr } = await supabase
        .from('emp_cronograma_versoes')
        .select('contrato_id')
        .eq('id', versaoId)
        .single();
      if (versaoErr || !versaoData) throw new Error('Versão não encontrada');

      const { data: contratoData, error: contratoErr } = await supabase
        .from('emp_contratos')
        .select('tipo_medicao, valor_global')
        .eq('id', versaoData.contrato_id)
        .single();
      if (contratoErr) throw contratoErr;

      const tipoMedicao = contratoData?.tipo_medicao || 'preco_unitario';
      const valorGlobal = contratoData?.valor_global || 0;

      // 3. Fetch all linked activities
      const { data: atividades, error: ativError } = await supabase
        .from('emp_cronograma_atividades')
        .select('id, planilha_item_id, peso_fisico')
        .eq('versao_id', versaoId)
        .eq('status', 'ativo')
        .not('planilha_item_id', 'is', null);

      if (ativError) throw ativError;

      if (atividades && atividades.length > 0) {
        const itemIds = [...new Set(atividades.map(a => a.planilha_item_id!))];
        const { data: planilhaItens } = await supabase
          .from('emp_planilha_itens')
          .select('id, valor_total')
          .in('id', itemIds);

        const valorMap = new Map((planilhaItens || []).map(i => [i.id, i.valor_total || 0]));

        // Count activities per item (for PPU 1:1 detection)
        const itemLinkCount: Record<string, number> = {};
        if (tipoMedicao === 'preco_unitario') {
          for (const ativ of atividades) {
            if (ativ.planilha_item_id) {
              itemLinkCount[ativ.planilha_item_id] = (itemLinkCount[ativ.planilha_item_id] || 0) + 1;
            }
          }
        }

        // Recalculate each activity's valor_ponderado
        let recalculated = 0;
        for (const ativ of atividades) {
          const pesoFisico = ativ.peso_fisico || 0;
          let valorPonderado = 0;

          if (tipoMedicao === 'preco_unitario') {
            const valorTotalItem = valorMap.get(ativ.planilha_item_id!) || 0;
            const linkCount = itemLinkCount[ativ.planilha_item_id!] || 1;
            if (linkCount === 1) {
              valorPonderado = valorTotalItem; // 1:1
            } else {
              valorPonderado = (pesoFisico / 100) * valorTotalItem; // N:1
            }
          } else if (tipoMedicao === 'epc') {
            valorPonderado = (pesoFisico / 100) * valorGlobal;
          } else {
            const valorTotalItem = valorMap.get(ativ.planilha_item_id!) || 0;
            valorPonderado = (pesoFisico / 100) * valorTotalItem;
          }

          const { error: updError } = await supabase
            .from('emp_cronograma_atividades')
            .update({ valor_ponderado: valorPonderado })
            .eq('id', ativ.id);

          if (!updError) recalculated++;
        }
        console.log(`[ativar-cronograma] Recalculated valor_ponderado for ${recalculated}/${atividades.length} activities (tipo=${tipoMedicao})`);
      }

      // 4. Activate the version
      const { error } = await supabase
        .from('emp_cronograma_versoes')
        .update({ status: 'vigente', updated_at: new Date().toISOString() })
        .eq('id', versaoId);
      if (error) throw error;

      return gatesResult?.gates || {};
    },
    onSuccess: (gates) => {
      queryClient.invalidateQueries({ queryKey: ['emp-cronograma-versoes'] });
      queryClient.invalidateQueries({ queryKey: ['emp-cronograma-atividades'] });
      queryClient.invalidateQueries({ queryKey: ['emp-linter'] });

      const cobFin = gates?.cobertura_financeira;
      const desc = cobFin != null
        ? `Valores ponderados recalculados. Cobertura financeira: ${cobFin}%`
        : 'Valores ponderados recalculados com sucesso.';
      toast({ title: 'Versão do cronograma ativada', description: desc });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao ativar', description: err.message, variant: 'destructive' });
    },
  });
}

/** Auto-vinculação controlada como reconciliação */
export function useEmpAutoVincular() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { versao_id: string; planilha_versao_id: string; aplicar?: boolean; contrato_id?: string }) => {
      const { data, error } = await supabase.rpc('emp_auto_vincular_cronograma_planilha', {
        p_versao_id: params.versao_id,
        p_planilha_versao_id: params.planilha_versao_id,
        p_aplicar: params.aplicar ?? false,
        p_contrato_id: params.contrato_id ?? null,
      });
      if (error) throw error;
      return data as unknown as VinculoReport;
    },
    onSuccess: (data, variables) => {
      if (variables.aplicar) {
        queryClient.invalidateQueries({ queryKey: ['emp-cronograma-atividades'] });
        toast({
          title: `Auto-vinculação aplicada`,
          description: `${data.novas_vinculacoes || 0} novos vínculos criados. Cobertura: ${data.cobertura_financeira}%`,
        });
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Erro na auto-vinculação', description: err.message, variant: 'destructive' });
    },
  });
}
